import { MouseEventHandler, useMemo, useState } from 'react';
import { pipe } from 'fp-ts/function';
import { map } from 'fp-ts/Array';
import { PortfolioEnterpriseRelationshipResult } from '@mews-commander/data-access';
import {
    debounce,
    DebounceTimeoutValues,
    DropdownItem,
    InputValue,
    ReducerActionType,
    tableStateReducer,
    useTableState,
} from '@optimus-web/core';
import { uniqById } from '@mews/fp';
import { Option } from '@mews-commander/types';
import { createOption } from '@mews-commander/ui';
import { useEnterpriseSearchQuery } from '../../queries/useEnterpriseSearchQuery';

const renderOptionListItems = ({ value, label }: Option) => (
    <DropdownItem key={value} value={value as string} label={label} />
);

const mapPropertiesToOptions = (properties: Array<PortfolioEnterpriseRelationshipResult>) =>
    properties.filter(Boolean).map((property) =>
        createOption({
            label: property.name,
            value: property.id,
        })
    );

export const useEnterpriseOptions = (defaultValue: Array<PortfolioEnterpriseRelationshipResult>) => {
    const [selectedValues, setSelectedValues] = useState<Array<PortfolioEnterpriseRelationshipResult>>(defaultValue);
    const [tableState, setTableState] = useTableState<PortfolioEnterpriseRelationshipResult>();

    const {
        data: profileData,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useEnterpriseSearchQuery(tableState);

    const handleLoadMoreClick: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isFetching || isFetchingNextPage) return;

        void fetchNextPage();
    };

    const loadedOptions = uniqById([...(profileData?.pages?.flatMap((p) => p.data) || []), ...selectedValues]);

    const handleChange = (values: Array<InputValue>) =>
        setSelectedValues(
            (Array.isArray(values) ? values : [values]).map(
                (v) => loadedOptions.find((p) => p.id === v) as PortfolioEnterpriseRelationshipResult
            )
        );

    const selectOptions = useMemo(
        () => (profileData?.pages ? pipe(loadedOptions, mapPropertiesToOptions, map(renderOptionListItems)) : null),
        [profileData?.pages, loadedOptions]
    );

    const onSearch = debounce((search: string) => {
        setTableState((state) =>
            tableStateReducer(state, {
                type: ReducerActionType.GlobalSearch,
                payload: { search },
            })
        );
    }, DebounceTimeoutValues.textInput);

    return {
        onSearch,
        handleChange,
        handleLoadMoreClick,
        hasNextPage,
        isFetchingNextPage,
        selectOptions,
    };
};
